import { useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import useCurrentOrganization from 'hooks/useCurrentOrganization';
import useFetch from 'hooks/useFetch';
import useGlobalStore from 'hooks/useGlobalStore';
import useToast from 'hooks/useToast';
import { SUCCESS } from 'hooks/useToast/constants';
import useUsers from 'hooks/useUsers';

import useTranslation from './hooks/useTranslate';

import { EDIT_ACCOUNT_API } from './constants';
import type { EditUserData, ResolveUserData, UseAccount, User } from './types';

const useAccount: UseAccount = () => {
    const { getValue, setValue } = useGlobalStore({
        namespace: 'useAccount'
    });
    const { broadcastToast } = useToast();
    const { UPDATE_USER_SUCCESS } = useTranslation();

    const { user: userToken } = useAuth0();
    const { currentOrganization } = useCurrentOrganization();
    const { authenticatedPut } = useFetch();
    const { fetchUser } = useUsers();

    const saveMyUserToStore = useCallback(
        (value?: User | null) => setValue({ myUser: value }),
        [setValue]
    );

    const user = getValue('myUser');

    const resolveUserData: ResolveUserData = useCallback(
        async ({ force = false } = {}) => {
            if (!force && user) {
                return;
            }

            const { data, error } = await fetchUser({ userId: 'me' });

            if (error) {
                saveMyUserToStore(null);

                return;
            }

            saveMyUserToStore(data);
        },
        [fetchUser, saveMyUserToStore, user]
    );

    const updateUserData = useCallback(
        async (data: EditUserData) => {
            const { firstName, lastName, phone } = data;

            const requestData = {
                firstName,
                lastName,
                phoneNumber: phone
                    ? {
                          countryCode: '1',
                          phone: phone?.replaceAll(/[-() ]/g, '')
                      }
                    : undefined
            };

            const response = await authenticatedPut(EDIT_ACCOUNT_API, JSON.stringify(requestData));

            if (response.data) {
                broadcastToast({
                    text: UPDATE_USER_SUCCESS,
                    type: SUCCESS
                });

                saveMyUserToStore(response.data);
                await resolveUserData();
            }

            return response;
        },
        [UPDATE_USER_SUCCESS, authenticatedPut, broadcastToast, resolveUserData, saveMyUserToStore]
    );

    const isEmailVerified = userToken?.email_verified || Boolean(currentOrganization);
    const isAccountComplete =
        user === null
            ? false
            : user === undefined || (user?.firstName !== '' && user?.lastName !== '');
    const isOnboardingComplete =
        isEmailVerified && isAccountComplete && typeof currentOrganization !== 'undefined';

    return {
        isAccountComplete,
        isEmailVerified,
        isOnboardingComplete,
        resolveUserData,
        saveMyUserToStore,
        updateUserData,
        user
    };
};

export default useAccount;
